import {ReactNode, useMemo, useState} from 'react';
import {Box, Stack, Typography} from '@mui/material';
import {Modal} from '../modal/modal';
import FlatInput from '../flat-input/flat-input';
import {FlatSelectDropdown, Option} from '../flat-select-dropdown/flat-select-dropdown';
import {ReminderRecurrenceType} from '../../backend/interfaces/reminder-recurrence-type';
import {ReminderResponseType} from '../../backend/enums/reminder-response-type';
import {Reminder} from '../../backend/interfaces/reminder';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {
  ReminderFormValues,
  getReminderFormValues,
  sanitizeReminderMessage,
  sanitizeReminderTitle,
} from '../../screens/private/office/informal-caregiver/reminder/reminder-form.utils';

export const ReminderFormDialog = ({
  open,
  reminder,
  isSubmitting,
  onClose,
  onSave,
}: ReminderFormDialogProps) => {
  const {t} = useTranslation();

  return (
    <ReminderForm reminder={reminder} isSubmitting={isSubmitting} onSave={onSave}>
      {(form, handleSubmit) => (
        <Modal
          open={open}
          onClose={onClose}
          title={reminder ? t(translations.reminders.edit) : t(translations.reminders.add)}
          maxWidth="sm"
          fullWidth
          actions={[
            {
              label: t(translations.reminders.cancel),
              type: 'destructive',
              disabled: isSubmitting,
              onClick: onClose,
            },
            {
              label: t(translations.reminders.form.save),
              type: 'nondestructive',
              loading: isSubmitting,
              disabled: isSubmitting,
              onClick: handleSubmit,
            },
          ]}
        >
          {form}
        </Modal>
      )}
    </ReminderForm>
  );
};

export const ReminderForm = ({
  reminder,
  isSubmitting,
  onSave,
  fieldErrors,
  onInvalid,
  children,
}: ReminderFormProps) => {
  const {t} = useTranslation();
  const [values, setValues] = useState<ReminderFormValues>(() => getReminderFormValues(reminder));
  const [showValidation, setShowValidation] = useState(false);

  const recurrenceOptions = useMemo<Option[]>(() => {
    const options = [
      {key: ReminderRecurrenceType.Once, label: t(translations.reminders.recurrence.once)},
      {
        key: ReminderRecurrenceType.Hourly,
        label: t(translations.reminders.recurrence.hourly, {count: 1}),
      },
      {
        key: ReminderRecurrenceType.Daily,
        label: t(translations.reminders.recurrence.daily, {count: 1}),
      },
      {
        key: ReminderRecurrenceType.Weekly,
        label: t(translations.reminders.recurrence.weekly, {count: 1}),
      },
      {
        key: ReminderRecurrenceType.Monthly,
        label: t(translations.reminders.recurrence.monthly, {count: 1}),
      },
    ];

    if (reminder && !options.some((option) => option.key === reminder.recurrenceType)) {
      options.unshift({
        key: reminder.recurrenceType,
        label: t(translations.reminders.recurrence.current_unsupported, {
          value: reminder.recurrenceType,
        }),
      });
    }

    return options;
  }, [reminder, t]);

  const responseOptions = useMemo<Option[]>(() => {
    const options = [
      {key: ReminderResponseType.YesOrNo, label: t(translations.reminders.response_types.yes_no)},
      {key: ReminderResponseType.OK, label: t(translations.reminders.response_types.ok)},
    ];

    if (reminder && !options.some((option) => option.key === reminder.responseType)) {
      options.unshift({
        key: reminder.responseType,
        label: t(translations.reminders.recurrence.current_unsupported, {
          value: reminder.responseType,
        }),
      });
    }

    return options;
  }, [reminder, t]);

  const recurrenceCountOptions = useMemo<Option[]>(() => {
    const options = REMINDER_RECURRENCE_COUNTS.map((count) => ({
      key: `${count}`,
      label: `${count}`,
    }));

    if (!options.some((option) => option.key === `${values.recurrenceCount}`)) {
      options.unshift({key: `${values.recurrenceCount}`, label: `${values.recurrenceCount}`});
    }

    return options;
  }, [values.recurrenceCount]);

  const originalValues = reminder ? getReminderFormValues(reminder) : undefined;
  const dateTimeIsUnchanged =
    originalValues?.date === values.date && originalValues.time === values.time;
  const parsedStartAt = new Date(`${values.date}T${values.time}:00`);
  const isStartAtValid =
    !Number.isNaN(parsedStartAt.getTime()) &&
    (dateTimeIsUnchanged || parsedStartAt.getTime() >= Date.now());
  const dateError = !values.date
    ? t(translations.reminders.form.date_required)
    : !values.time || Number.isNaN(parsedStartAt.getTime())
      ? t(translations.reminders.form.invalid_date_time)
      : !isStartAtValid &&
          new Date(`${values.date}T00:00:00`).getTime() < new Date().setHours(0, 0, 0, 0)
        ? t(translations.reminders.form.date_in_past)
        : undefined;
  const timeError = !values.time
    ? t(translations.reminders.form.time_required)
    : !Number.isNaN(parsedStartAt.getTime()) && !isStartAtValid && !dateError
      ? t(translations.reminders.form.time_in_past)
      : undefined;
  const titleError =
    sanitizeReminderTitle(values.title).length === 0
      ? t(translations.reminders.form.title_required)
      : sanitizeReminderTitle(values.title).length > REMINDER_TITLE_MAX_CHARACTERS
        ? t(translations.reminders.form.title_too_long, {max: REMINDER_TITLE_MAX_CHARACTERS})
        : undefined;
  const messageError =
    sanitizeReminderMessage(values.message).length === 0
      ? t(translations.reminders.form.message_required)
      : sanitizeReminderMessage(values.message).length > REMINDER_MESSAGE_MAX_CHARACTERS
        ? t(translations.reminders.form.message_too_long, {max: REMINDER_MESSAGE_MAX_CHARACTERS})
        : undefined;
  const contextualErrors = fieldErrors?.(values);
  const hasValidationErrors = Boolean(titleError || messageError || dateError || timeError);

  const handleSubmit = async () => {
    setShowValidation(true);
    const startAtStillValid =
      dateTimeIsUnchanged ||
      (!Number.isNaN(parsedStartAt.getTime()) && parsedStartAt.getTime() >= Date.now());
    if (hasValidationErrors || !startAtStillValid) {
      onInvalid?.();
      return false;
    }
    if (isSubmitting) return false;
    await onSave(values);
    return true;
  };

  const handleRecurrenceChange = (option: Option) => {
    const recurrenceType = option.key as ReminderRecurrenceType;
    setValues((current) => ({
      ...current,
      recurrenceType,
      recurrenceCount: recurrenceType === ReminderRecurrenceType.Once ? 1 : current.recurrenceCount,
    }));
  };

  const helperText = (value: string, max: number) => (
    <Typography variant="caption" color={value.length > max ? 'error' : 'text.secondary'}>
      {value.length}/{max}
    </Typography>
  );

  const form = (
    <Stack spacing={2.5}>
      <Box>
        <FlatInput
          required
          label={t(translations.reminders.form.title)}
          placeholder={t(translations.reminders.form.title_placeholder)}
          value={values.title}
          onChange={(event) => setValues((current) => ({...current, title: event.target.value}))}
          errors={showValidation ? titleError : undefined}
          inputProps={{maxLength: REMINDER_TITLE_MAX_CHARACTERS}}
        />
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 0.5}}>
          {helperText(sanitizeReminderTitle(values.title), REMINDER_TITLE_MAX_CHARACTERS)}
        </Box>
      </Box>
      <Box>
        <FlatInput
          required
          multiline
          minRows={3}
          maxRows={6}
          label={t(translations.reminders.form.message)}
          placeholder={t(translations.reminders.form.message_placeholder)}
          value={values.message}
          onChange={(event) => setValues((current) => ({...current, message: event.target.value}))}
          errors={showValidation ? messageError : undefined}
          inputProps={{maxLength: REMINDER_MESSAGE_MAX_CHARACTERS}}
        />
        <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 0.5}}>
          {helperText(sanitizeReminderMessage(values.message), REMINDER_MESSAGE_MAX_CHARACTERS)}
        </Box>
      </Box>
      <FlatSelectDropdown
        required
        label={t(translations.reminders.form.response_type)}
        options={responseOptions}
        value={responseOptions.find((option) => option.key === values.responseType)}
        onChange={(option) =>
          setValues((current) => ({
            ...current,
            responseType: option.key as ReminderResponseType,
          }))
        }
      />
      <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
        <FlatInput
          required
          fullWidth
          label={t(translations.reminders.form.date)}
          type="date"
          value={values.date}
          onChange={(event) => setValues((current) => ({...current, date: event.target.value}))}
          errors={(showValidation ? dateError : undefined) || contextualErrors?.date}
        />
        <FlatInput
          required
          fullWidth
          label={t(translations.reminders.form.time)}
          type="time"
          value={values.time}
          onChange={(event) => setValues((current) => ({...current, time: event.target.value}))}
          errors={(showValidation ? timeError : undefined) || contextualErrors?.time}
        />
      </Stack>
      <FlatSelectDropdown
        required
        label={t(translations.reminders.form.recurrence)}
        options={recurrenceOptions}
        value={recurrenceOptions.find((option) => option.key === values.recurrenceType)}
        onChange={handleRecurrenceChange}
        errors={contextualErrors?.recurrenceType}
      />
      {values.recurrenceType !== ReminderRecurrenceType.Once && (
        <FlatSelectDropdown
          required
          label={t(translations.reminders.form.recurrence_count)}
          errors={contextualErrors?.recurrenceCount}
          options={recurrenceCountOptions}
          value={recurrenceCountOptions.find(
            (option) => option.key === `${values.recurrenceCount}`
          )}
          onChange={(option) =>
            setValues((current) => ({...current, recurrenceCount: Number(option.key) || 1}))
          }
        />
      )}
    </Stack>
  );

  return <>{children(form, handleSubmit)}</>;
};

const REMINDER_TITLE_MAX_CHARACTERS = 80;
const REMINDER_MESSAGE_MAX_CHARACTERS = 250;
const REMINDER_RECURRENCE_COUNTS = Array.from({length: 12}, (_, index) => index + 1);

interface ReminderFormProps {
  reminder?: Reminder;
  isSubmitting: boolean;
  onSave: (values: ReminderFormValues) => Promise<void>;
  onInvalid?: () => void;
  fieldErrors?: (
    values: ReminderFormValues
  ) => Partial<Record<'date' | 'time' | 'recurrenceType' | 'recurrenceCount', string>>;
  children: (form: ReactNode, handleSubmit: () => Promise<boolean>) => ReactNode;
}

interface ReminderFormDialogProps extends Omit<ReminderFormProps, 'children'> {
  open: boolean;
  onClose: () => void;
}
